/**
 * Scroll event handlers for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

/**
 * Creates scroll handler that calls throttled update function
 * @param {Function} throttledUpdateScrollMetrics - Throttled function to update scroll metrics
 * @returns {Function} Handler function for scroll events
 */
export const createScrollHandler = (throttledUpdateScrollMetrics) => {
  return () => throttledUpdateScrollMetrics();
};

/**
 * Creates move handler for custom scrollbar thumb drag.
 * Maps pointer delta to scroll delta using the thumb vs track geometry (standard scrollbar math).
 * @param {Object} dragState - Current drag state
 * @param {Object} scrollAreaRef - Reference to scroll area element
 * @param {{ current: HTMLElement | null } | null} vTrackRef - Vertical scrollbar track
 * @param {{ current: HTMLElement | null } | null} hTrackRef - Horizontal scrollbar track
 */
export const createMoveHandler = (dragState, scrollAreaRef, vTrackRef, hTrackRef) => {
  return (e) => {
    const el = scrollAreaRef.current;
    if (!el) return;

    if (dragState.axis === "v") {
      const track = vTrackRef?.current;
      const thumb = track?.firstElementChild;
      const trackH = track?.clientHeight ?? 0;
      const thumbH = thumb?.clientHeight ?? 16;
      const span = Math.max(1, trackH - thumbH);

      const delta = e.clientY - dragState.startClient;
      const contentScrollable = Math.max(0, el.scrollHeight - el.clientHeight);
      if (contentScrollable <= 0) return;

      const newScrollTop =
        dragState.startScroll + (delta / span) * contentScrollable;
      const clampedTop = Math.max(0, Math.min(contentScrollable, newScrollTop));
      el.scrollTop = clampedTop;
    } else if (dragState.axis === "h") {
      const track = hTrackRef?.current;
      const thumb = track?.firstElementChild;
      const trackW = track?.clientWidth ?? 0;
      const thumbW = thumb?.clientWidth ?? 16;
      const span = Math.max(1, trackW - thumbW);

      const delta = e.clientX - dragState.startClient;
      const contentScrollable = Math.max(0, el.scrollWidth - el.clientWidth);
      if (contentScrollable <= 0) return;

      const newScrollLeft =
        dragState.startScroll + (delta / span) * contentScrollable;
      const clampedLeft = Math.max(
        0,
        Math.min(contentScrollable, newScrollLeft),
      );
      el.scrollLeft = clampedLeft;
    }
  };
};

/**
 * Creates up handler for ending drag scrolling
 * @param {Function} setDragState - Function to set drag state
 * @returns {Function} Handler function for up events
 */
export const createUpHandler = (setDragState) => {
  return () => {
    setDragState(null);
  };
};
